import "@/App.css";
// Image Edit transport contracts are installed once, before any workspace can
// submit. This keeps source binding / lineage / safe approval consistent across
// today's workspace and any future Image Edit entry point.
import "./lib/imageEditRuntime";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProjectProvider } from "./contexts/ProjectContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import ProductionRoute from "./components/ProductionRoute";
import OperatorRoute from "./components/OperatorRoute";
import AsistEditGate from "./components/AsistEditGate";
import UpdateBanner from "./components/UpdateBanner";
import NavigationGuard from "./components/NavigationGuard";
import LanguageSwitch from "./components/LanguageSwitch";
// Con code-splitting, una pestaña abierta durante un deploy pide chunks que ya
// no existen (medido: el bundle del build anterior devuelve 404). Sin esta red
// el operador se come una pantalla en blanco al primer clic. Va en el shell,
// no en un chunk: tiene que estar cargada ANTES de que falle nada.
import ChunkErrorBoundary, { clearChunkReloadFlag } from "./components/ChunkErrorBoundary";

// Keep the authenticated shell small; load each workspace when opened.
const LoginPage = lazy(() => import("./components/LoginPage"));
const SeedanceVFXTool = lazy(() => import("./components/SeedanceVFXTool"));
const JobsPage = lazy(() => import("./components/seedance/JobsPage"));
const SharedPage = lazy(() => import("./components/seedance/SharedPage"));
const AdminPage = lazy(() => import("./components/AdminPage"));
const ProjectsPage = lazy(() => import("./components/ProjectsPage"));
const VFXKeyframesPage = lazy(() => import("./components/VFXKeyframesPage"));
const VFXKeyframeDetailPage = lazy(() => import("./components/VFXKeyframeDetailPage"));
const VFXAutopilotPage = lazy(() => import("./pages/VFXAutopilotPage"));
const ReviewPortal = lazy(() => import("./components/ReviewPortal"));
const NamedReviewPortal = lazy(() => import("./components/NamedReviewPortal"));
const IngestPage = lazy(() => import("./components/IngestPage"));
const PipelineScoresPage = lazy(() => import("./components/PipelineScoresPage"));
const AssetLibraryPage = lazy(() => import("./components/AssetLibraryPage"));
const AsistEditPage = lazy(() => import("./pages/AsistEditPage"));
const OrcaLabPage = lazy(() => import("./pages/OrcaLabPage"));
const OrcaBlockingPage = lazy(() => import("./pages/OrcaBlockingPage"));
const ImageEditWorkspace = lazy(() => import("./components/ImageEditWorkspace"));
const ProductionPage = lazy(() => import("./pages/ProductionPage"));
const PluginAuthPage = lazy(() => import("./components/PluginAuthPage"));

const EditorialContextPage = lazy(() => import("./pages/EditorialContextPage"));
// TelePrompter: panel + iconos flotantes FUERA de las rutas, para que una
// conversación siga viva al cambiar de plano o de página.
const TeleprompterHost = lazy(() => import("./components/seedance/TeleprompterPanel")
  .then((m) => ({ default: m.TeleprompterHost })));

function AppRouter() {
  // Una carga sana borra la marca anti-bucle: si se quedara puesta de un
  // incidente viejo, el próximo deploy enseñaría el mensaje sin intentar
  // siquiera la recarga que casi siempre lo resuelve.
  useEffect(() => { clearChunkReloadFlag(); }, []);
  return (
    <ChunkErrorBoundary>
    <Suspense fallback={<div role="status" aria-live="polite" className="min-h-screen bg-[#09090b] text-zinc-400 flex items-center justify-center">Loading…</div>}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Sin ProtectedRoute a propósito: ese guardián redirige a /login y se
          come el `?code=`. La página gestiona su propio gate mandando al
          OAuth con `next`, para volver AQUÍ y no al editor. */}
      <Route path="/plugin-auth" element={<PluginAuthPage />} />
      <Route path="/" element={<ProtectedRoute><AsistEditGate><SeedanceVFXTool /></AsistEditGate></ProtectedRoute>} />
      <Route path="/asist-edit" element={<ProtectedRoute><AsistEditPage /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
      <Route path="/shared" element={<ProtectedRoute><SharedPage /></ProtectedRoute>} />
      <Route path="/projects" element={<ProductionRoute><ProjectsPage /></ProductionRoute>} />
      <Route path="/vfx" element={<ProductionRoute><VFXKeyframesPage /></ProductionRoute>} />
      <Route path="/vfx/:id" element={<ProductionRoute><VFXKeyframeDetailPage /></ProductionRoute>} />
      <Route path="/vfx-autopilot" element={<ProductionRoute><VFXAutopilotPage /></ProductionRoute>} />
      <Route path="/production" element={<ProductionRoute><ProductionPage /></ProductionRoute>} />
      <Route path="/ingest" element={<OperatorRoute><IngestPage /></OperatorRoute>} />
      <Route path="/scores" element={<ProductionRoute><PipelineScoresPage /></ProductionRoute>} />
      <Route path="/editorial" element={<ProtectedRoute><EditorialContextPage /></ProtectedRoute>} />
      <Route path="/assets" element={<ProtectedRoute><AssetLibraryPage /></ProtectedRoute>} />
      <Route path="/lab" element={<ProductionRoute><OrcaLabPage /></ProductionRoute>} />
      <Route path="/orca-blocking" element={<ProductionRoute><OrcaBlockingPage /></ProductionRoute>} />
      <Route path="/image-edit" element={<ProductionRoute><ImageEditWorkspace /></ProductionRoute>} />
      <Route path="/review/:token" element={<ReviewPortal />} />
      <Route path="/review-workflow/:token" element={<NamedReviewPortal />} />
      <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
    </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        {/* Avisa antes de abandonar una pantalla con trabajo sin enviar (enlaces,
            atrás/adelante). useBlocker exige un data router; ver lib/navigationGuard. */}
        <NavigationGuard />
        <AuthProvider>
          <ProjectProvider>
            <AppRouter />
            <Suspense fallback={null}><TeleprompterHost /></Suspense>
          </ProjectProvider>
        </AuthProvider>
      </BrowserRouter>
      <div data-testid="global-language-switch" style={{
        position: "fixed", right: 14, bottom: 14, zIndex: 10020,
        boxShadow: "0 4px 18px rgba(0,0,0,.35)", borderRadius: 7,
      }}>
        <LanguageSwitch compact />
      </div>
      {/* SW2 — banner de actualización, por encima de todo, fuera del router */}
      <UpdateBanner />
    </div>
  );
}

export default App;
